import type {
  MotionLinkVisualizationData,
  MotionLinkVisualizationSample,
} from './motionlinkJsonLoader.js';

export interface MotionLinkCleaningOptions {
  enabled: boolean;
  strength: 'light' | 'balanced' | 'strong';
  smoothingWindow?: number;
  deadbandMeters?: number;
  outlierThresholdMeters?: number;
  maxPlausibleSpeedMetersPerSecond?: number;
  minimumConfidence?: number;
}

export interface MotionLinkCleaningStats {
  enabled: boolean;
  strength: MotionLinkCleaningOptions['strength'];
  rawDistance: number;
  cleanedDistance: number;
  impossibleJumpSamplesAdjusted: number;
  outlierSamplesAdjusted: number;
  deadbandSamplesAdjusted: number;
  lowConfidenceSamplesAdjusted: number;
  smoothingWindow: number;
  warnings: string[];
}

export interface MotionLinkCleaningResult {
  data: MotionLinkVisualizationData;
  stats: MotionLinkCleaningStats;
}

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

function distance(a: MotionLinkVisualizationSample['position'], b: MotionLinkVisualizationSample['position']): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

function pathDistance(samples: readonly MotionLinkVisualizationSample[]): number {
  let total = 0;
  for (let index = 1; index < samples.length; index++) {
    total += distance(samples[index - 1]!.position, samples[index]!.position);
  }
  return total;
}

function boundsFor(samples: readonly MotionLinkVisualizationSample[]): MotionLinkVisualizationData['bounds'] {
  if (!samples.length) return { minX: 0, maxX: 0, minZ: 0, maxZ: 0 };
  return {
    minX: Math.min(...samples.map((sample) => sample.position.x)),
    maxX: Math.max(...samples.map((sample) => sample.position.x)),
    minZ: Math.min(...samples.map((sample) => sample.position.z)),
    maxZ: Math.max(...samples.map((sample) => sample.position.z)),
  };
}

function defaultsFor(strength: MotionLinkCleaningOptions['strength']): Required<Omit<MotionLinkCleaningOptions, 'enabled' | 'strength'>> {
  if (strength === 'light') {
    return {
      smoothingWindow: 3,
      deadbandMeters: 0.006,
      outlierThresholdMeters: 0.18,
      maxPlausibleSpeedMetersPerSecond: 18,
      minimumConfidence: 0.22,
    };
  }
  if (strength === 'strong') {
    return {
      smoothingWindow: 9,
      deadbandMeters: 0.025,
      outlierThresholdMeters: 0.075,
      maxPlausibleSpeedMetersPerSecond: 6,
      minimumConfidence: 0.45,
    };
  }
  return {
    smoothingWindow: 5,
    deadbandMeters: 0.012,
    outlierThresholdMeters: 0.12,
    maxPlausibleSpeedMetersPerSecond: 10,
    minimumConfidence: 0.32,
  };
}

function cloneSample(sample: MotionLinkVisualizationSample): MotionLinkVisualizationSample {
  return {
    timestampMs: sample.timestampMs,
    position: { ...sample.position },
    ...(sample.rotation ? { rotation: { ...sample.rotation } } : {}),
    ...(sample.confidence === undefined ? {} : { confidence: sample.confidence }),
    ...(sample.motionType === undefined ? {} : { motionType: sample.motionType }),
  };
}

function mixPosition(
  a: MotionLinkVisualizationSample['position'],
  b: MotionLinkVisualizationSample['position'],
  t = 0.5,
): MotionLinkVisualizationSample['position'] {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  };
}

function smoothSamples(
  samples: readonly MotionLinkVisualizationSample[],
  windowSize: number,
  preserveIndices = new Set<number>(),
): MotionLinkVisualizationSample[] {
  const halfWindow = Math.max(1, Math.floor(windowSize / 2));
  return samples.map((sample, index) => {
    if (preserveIndices.has(index)) return sample;
    if (index === 0 || index === samples.length - 1) return sample;
    const start = Math.max(0, index - halfWindow);
    const end = Math.min(samples.length, index + halfWindow + 1);
    const window = samples.slice(start, end);
    const weighted = window.reduce((acc, item) => {
      const confidenceWeight = item.confidence === undefined ? 1 : clamp(item.confidence, 0.15, 1);
      const distanceWeight = 1 / (1 + Math.abs(item.timestampMs - sample.timestampMs) / 1000);
      const weight = confidenceWeight * distanceWeight;
      acc.weight += weight;
      acc.x += item.position.x * weight;
      acc.y += item.position.y * weight;
      acc.z += item.position.z * weight;
      acc.pitch += (item.rotation?.pitch ?? sample.rotation?.pitch ?? 0) * weight;
      acc.yaw += (item.rotation?.yaw ?? sample.rotation?.yaw ?? 0) * weight;
      acc.roll += (item.rotation?.roll ?? sample.rotation?.roll ?? 0) * weight;
      return acc;
    }, { weight: 0, x: 0, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0 });

    const weight = Math.max(1e-6, weighted.weight);
    return {
      ...sample,
      position: {
        x: weighted.x / weight,
        y: weighted.y / weight,
        z: weighted.z / weight,
      },
      ...(sample.rotation
        ? {
            rotation: {
              pitch: weighted.pitch / weight,
              yaw: weighted.yaw / weight,
              roll: weighted.roll / weight,
            },
          }
        : {}),
    };
  });
}

export function cleanMotionLinkVisualizationData(
  data: MotionLinkVisualizationData,
  options: MotionLinkCleaningOptions,
): MotionLinkCleaningResult {
  const defaults = defaultsFor(options.strength);
  const smoothingWindow = Math.max(1, Math.round(options.smoothingWindow ?? defaults.smoothingWindow));
  const deadbandMeters = options.deadbandMeters ?? defaults.deadbandMeters;
  const outlierThresholdMeters = options.outlierThresholdMeters ?? defaults.outlierThresholdMeters;
  const maxPlausibleSpeedMetersPerSecond = options.maxPlausibleSpeedMetersPerSecond ?? defaults.maxPlausibleSpeedMetersPerSecond;
  const minimumConfidence = options.minimumConfidence ?? defaults.minimumConfidence;
  const rawDistance = pathDistance(data.samples);
  const warnings: string[] = [];

  if (!options.enabled || data.samples.length < 3) {
    return {
      data,
      stats: {
        enabled: options.enabled,
        strength: options.strength,
        rawDistance,
        cleanedDistance: rawDistance,
        impossibleJumpSamplesAdjusted: 0,
        outlierSamplesAdjusted: 0,
        deadbandSamplesAdjusted: 0,
        lowConfidenceSamplesAdjusted: 0,
        smoothingWindow,
        warnings: options.enabled && data.samples.length > 0 && data.samples.length < 3
          ? ['Not enough samples for motion cleanup.']
          : [],
      },
    };
  }

  let samples = data.samples.map(cloneSample);
  const impossibleJumpIndices = new Set<number>();
  let impossibleJumpSamplesAdjusted = 0;
  let outlierSamplesAdjusted = 0;
  let lowConfidenceSamplesAdjusted = 0;
  let deadbandSamplesAdjusted = 0;

  samples = samples.map((sample, index) => {
    if (index === 0 || index === samples.length - 1) return sample;
    const previous = samples[index - 1]!;
    const next = samples[index + 1]!;
    const midpoint = mixPosition(previous.position, next.position);
    const deviation = distance(sample.position, midpoint);
    const previousStep = distance(previous.position, sample.position);
    const nextStep = distance(sample.position, next.position);
    const neighborStep = Math.max(previousStep, nextStep);
    const routeDistance = previousStep + nextStep;
    const bypassDistance = distance(previous.position, next.position);
    const dtPreviousSeconds = Math.max(0.001, Math.abs(sample.timestampMs - previous.timestampMs) / 1000);
    const dtNextSeconds = Math.max(0.001, Math.abs(next.timestampMs - sample.timestampMs) / 1000);
    const maxPreviousStep = Math.max(outlierThresholdMeters, maxPlausibleSpeedMetersPerSecond * dtPreviousSeconds);
    const maxNextStep = Math.max(outlierThresholdMeters, maxPlausibleSpeedMetersPerSecond * dtNextSeconds);
    const isImpossibleJump =
      previousStep > maxPreviousStep &&
      nextStep > maxNextStep &&
      deviation > outlierThresholdMeters &&
      routeDistance > Math.max(outlierThresholdMeters * 2, bypassDistance * 3);
    const isOutlier = deviation > outlierThresholdMeters && deviation > neighborStep * 0.65;
    const isLowConfidence = sample.confidence !== undefined && sample.confidence < minimumConfidence;
    if (!isImpossibleJump && !isOutlier && !isLowConfidence) return sample;
    if (isImpossibleJump) {
      impossibleJumpSamplesAdjusted += 1;
      impossibleJumpIndices.add(index);
    }
    if (isOutlier) outlierSamplesAdjusted += 1;
    if (isLowConfidence) lowConfidenceSamplesAdjusted += 1;
    return {
      ...sample,
      position: isImpossibleJump
        ? midpoint
        : mixPosition(sample.position, midpoint, isOutlier ? 0.82 : 0.55),
    };
  });

  samples = smoothSamples(samples, smoothingWindow, impossibleJumpIndices);

  samples = samples.map((sample, index) => {
    if (index === 0) return sample;
    if (impossibleJumpIndices.has(index)) return sample;
    const previous = samples[index - 1]!;
    if (distance(previous.position, sample.position) >= deadbandMeters) return sample;
    deadbandSamplesAdjusted += 1;
    return {
      ...sample,
      position: { ...previous.position },
    };
  });

  const cleanedDistance = pathDistance(samples);
  if (impossibleJumpSamplesAdjusted > 0) warnings.push(`${impossibleJumpSamplesAdjusted} impossible jump sample${impossibleJumpSamplesAdjusted === 1 ? '' : 's'} removed.`);
  if (outlierSamplesAdjusted > 0) warnings.push(`${outlierSamplesAdjusted} spike sample${outlierSamplesAdjusted === 1 ? '' : 's'} softened.`);
  if (lowConfidenceSamplesAdjusted > 0) warnings.push(`${lowConfidenceSamplesAdjusted} low-confidence sample${lowConfidenceSamplesAdjusted === 1 ? '' : 's'} softened.`);
  if (deadbandSamplesAdjusted > 0) warnings.push(`${deadbandSamplesAdjusted} tiny jitter step${deadbandSamplesAdjusted === 1 ? '' : 's'} held.`);
  if (cleanedDistance < rawDistance * 0.55 && rawDistance > 0.05) {
    warnings.push('Strong cleanup reduced path distance significantly; compare against raw before trusting direction.');
  }

  return {
    data: {
      ...data,
      samples,
      bounds: boundsFor(samples),
      warnings: [...data.warnings, ...warnings],
    },
    stats: {
      enabled: true,
      strength: options.strength,
      rawDistance,
      cleanedDistance,
      impossibleJumpSamplesAdjusted,
      outlierSamplesAdjusted,
      deadbandSamplesAdjusted,
      lowConfidenceSamplesAdjusted,
      smoothingWindow,
      warnings,
    },
  };
}
